import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { useIsMobile } from '@/hooks/use-mobile';
import { getDeviceInfo } from '@/utils/device-detection';
import { isGoogleSignInBlocked, configureWebviewForAuth } from '@/utils/webview-detection';
import SocialAuthButtons from '@/components/auth/SocialAuthButtons';
import { isNativeApp } from '@/utils/platform';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { ShieldCheck } from 'lucide-react';

const Login = () => {
  const { t } = useLanguage();
  const { login, isAuthenticated, isLoading, pendingTwoFactor, pendingTwoFactorProvider, pendingTwoFactorToken, clearPendingTwoFactor, resetTwoFactor, loginWithGoogleCode, loginWithAppleCode, loginWithGoogle, loginWithApple } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const isInBlockingWebview = isGoogleSignInBlocked();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  
  // Gestion dynamique de l'état natif pour éviter les faux négatifs au chargement de l'URL
  const [native, setNative] = useState(isNativeApp());

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/agenda', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (isInBlockingWebview) {
      configureWebviewForAuth();
    }
  }, [isInBlockingWebview]);

  useEffect(() => {
    if (pendingTwoFactor) {
      setOtpCode('');
      setOtpError(null);
      setOtpOpen(true);
      clearPendingTwoFactor();
    }
  }, [pendingTwoFactor, clearPendingTwoFactor]);

  // Écoute de l'initialisation du pont Capacitor
  // Détection de l'environnement natif
  useEffect(() => {
    // Méthode 1: User Agent (la plus fiable si configurée côté natif)
    const uaContainsAppIdentifier = /median/i.test(navigator.userAgent);
    if (uaContainsAppIdentifier) {
      console.log('Native detected via User Agent.');
      setNative(true);
      return;
    }
    
    // Méthode 2: Pont Capacitor déjà prêt au chargement
    console.log('User Agent check failed. Waiting for Capacitor bridge...');
    if (isNativeApp()) {
      console.log('Capacitor bridge already available on initial check.');
      setNative(true);
      return;
    }
    
    // Méthode 3: Écouter l'événement `capacitorReady`
    const handleCapacitorReady = () => {
      console.log('`capacitorReady` event fired!');
      if (isNativeApp()) setNative(true);
    };
    window.addEventListener('capacitorReady', handleCapacitorReady);
    
    // Sécurité de secours si l'événement a été déclenché trop vite
    const timer = setTimeout(() => {
      console.log('Timeout check for Capacitor bridge.');
      if (isNativeApp()) setNative(true);
    }, 500); // Délai un peu plus long pour les appareils plus lents

    return () => {
      window.removeEventListener('capacitorReady', handleCapacitorReady);
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    getDeviceInfo();
    const animatedElements = document.querySelectorAll('.animated-element');
    animatedElements.forEach((el, i) => {
      setTimeout(() => {
        el.classList.add('appear');
      }, i * 100);
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const result = await login(username, password);
      //console.log('Login result:', result);
      if (result === '2fa_required') {
        setOtpCode('');
        setOtpError(null);
        setOtpOpen(true);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      console.error('Login submission error:', error);

      if (message.includes('two_factor') || message.includes('2fa')) {
        setOtpCode('');
        setOtpError(null);
        setOtpOpen(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpSubmit = async (code: string) => {
    setIsSubmitting(true);
    setOtpError(null);
    try {
      if (pendingTwoFactorProvider === 'google') {
        if (!pendingTwoFactorToken) {
          throw new Error('Missing Google token for 2FA continuation');
        }
        const result = await loginWithGoogle(pendingTwoFactorToken, code);
        if (result === '2fa_required') {
          setOtpCode('');
          setOtpError('Code invalide, veuillez réessayer.');
          return;
        }
      } else if (pendingTwoFactorProvider === 'apple') {
        if (!pendingTwoFactorToken) {
          throw new Error('Missing Apple token for 2FA continuation');
        }
        const result = await loginWithApple(pendingTwoFactorToken, code);
        if (result === '2fa_required') {
          setOtpCode('');
          setOtpError('Code invalide, veuillez réessayer.');
          return;
        }
      } else {
        const result = await login(username, password, code);
        if (result === '2fa_required') {
          setOtpCode('');
          setOtpError('Code invalide, veuillez réessayer.');
          return;
        }
      }

      setOtpOpen(false);
      resetTwoFactor();
    } catch (err) {
      setOtpCode('');
      setOtpError('Code invalide, veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const divider = (label: string) => (
    <div className="animated-element my-6">
      <div className="relative flex items-center justify-center">
        <Separator className="w-full" />
        <span className="absolute bg-background px-3 text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
      </div>
    </div>
  );

  const emailForm = (
    <form method="POST" className="space-y-5" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <label htmlFor="username" className="block text-sm font-medium">
          {t('login.username')}
        </label>
        <Input
          id="username"
          name="username"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          required
          className="w-full p-3 rounded-lg h-12"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label htmlFor="password" className="block text-sm font-medium">
            {t('common.password')}
          </label>
          <Link
            to="/forgot-password"
            className="text-sm text-primary hover:text-primary/90 transition-colors"
          >
            {t('common.forgotPassword')}
          </Link>
        </div>
        <Input
          id="password"
          type="password"
          name="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
          className="w-full p-3 rounded-lg h-12"
        />
      </div>

      {error && (
        <div className="text-red-500 text-sm text-center" role="alert">
          {error}
        </div>
      )}

      <div className="pt-1">
        <Button
          type="submit"
          className="w-full h-12 rounded-xl text-base"
          size="lg"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('common.loading')}
            </>
          ) : (
            t('common.login')
          )}
        </Button>
      </div>
    </form>
  );

  return (
    <>
    <div className="min-h-screen flex flex-col md:flex-row">
      <div className="flex-1 flex flex-col justify-center p-6 sm:p-8 md:p-12 lg:p-16">
        <div className="mx-auto w-full max-w-md">
          <div className="flex justify-between items-center mb-4">
            {!native && (
              <Link
                to="/"
                className="flex items-center text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                <span>{t('nav.home')}</span>
              </Link>
            )}
            <LanguageSwitcher />
          </div>

          {/* Mobile app-style logo header */}
          <div className="mb-8 md:hidden text-center">
            <img
              src="https://storage.googleapis.com/sportlogin/frontend/logo-blue.png"
              width="72"
              alt="SportLogin"
              className="mx-auto mb-1"
            />
            <h1 className="text-2xl font-bold tracking-tight mb-1">
              SPORTLOGIN
            </h1>
          </div>

          <div className="mb-8 hidden md:block">
            <h1 className="text-3xl font-bold tracking-tight mb-2">
              {t('login.title')}
            </h1>
            <p className="text-muted-foreground">
              {t('login.subtitle')}
            </p>
          </div>

          {isMobile ? (
            <>
              {emailForm}
              {divider(t('login.orContinueWith'))}
              <SocialAuthButtons appStyle />
            </>
          ) : (
            <>
              {emailForm}
              {divider(t('login.orContinueWith'))}
              <SocialAuthButtons />
            </>
          )}

          <div className="mt-8 text-center">
            <p className="text-muted-foreground text-sm">
              {t('login.noAccount')}{' '}
              <Link to="/register" className="text-primary hover:text-primary/90 transition-colors cursor-pointer">
                {t('login.createAccount')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="hidden md:block flex-1 bg-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 opacity-90" />
        <div className="absolute inset-0 bg-[url('https://storage.googleapis.com/sportlogin/frontend/images/3re04z6e8f06sfd.avif')] bg-cover bg-center opacity-20" />

        <div className="relative h-full flex items-center justify-center p-12">
          <div className="max-w-md text-white text-center">
            <div className="animated-element mb-6">
              <span className="inline-block bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium">
                SPORTLOGIN
              </span>
            </div>
            <h2 className="animated-element text-3xl font-bold mb-4">
              Take your coaching business to the next level
            </h2>
            <p className="animated-element text-white/80">
              Organize sessions, track client progress, and grow your business with our all-in-one platform.
            </p>
          </div>
        </div>
      </div>
    </div>

      {otpOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-2xl border border-white/80 bg-background p-6 shadow-2xl">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <ShieldCheck className="h-7 w-7 text-primary" />
            </div>
            <h2 className="text-center text-xl font-semibold">{t('login.twoFaTitle')}</h2>
            <p className="mt-2 text-center text-sm text-muted-foreground">
              {t('login.twoFaSubtitle')}
            </p>
            <div className="mt-6 flex flex-col items-center gap-4">
              <InputOTP
                maxLength={6}
                value={otpCode}
                disabled={isSubmitting}
                autoFocus
                onChange={(v) => {
                  setOtpCode(v);
                  if (v.length === 6) handleOtpSubmit(v);
                }}
              >
                <InputOTPGroup className="gap-2">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="h-14 w-11 text-2xl font-bold rounded-lg border-2 first:rounded-l-lg last:rounded-r-lg" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
              {otpError && <p className="text-sm text-destructive">{otpError}</p>}
              <Button className="w-full h-11" disabled={isSubmitting || otpCode.length !== 6} onClick={() => handleOtpSubmit(otpCode)}>
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {t('login.verif')}
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => {
                setOtpOpen(false);
                resetTwoFactor();
              }} disabled={isSubmitting}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Login;